import { useState, useEffect, useRef } from "react";
import { useModalManager } from "../../hooks/useModalManager";
import { useMerchant } from "../../context/merchant";
import CalendarModal from "./calendar";
import HistoryTable from "./table";
import HistorySummary from "./summary";
import EmptyStateComponent from "../../components/empty";
import { fetchOrders, fetchOrderDetails } from "../../api/axios/requests";
import OrderDetailsModal from "../../modals/order-details-modal";

export default function MerchantHistoryScreen() {
  const { isFullScreen, toggleFullScreen } = useMerchant();
  const { openModal, closeModal, currentModal } = useModalManager();

  const toLocalDateOnly = (dt) => {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, "0");
    const d = String(dt.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const [startDate, setStartDate] = useState(toLocalDateOnly(new Date()));
  const [endDate, setEndDate] = useState(toLocalDateOnly(new Date()));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [selectedModal, setSelectedModal] = useState("");

  // ✅ Order details state
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);

  // ✅ Cache: orderId -> orderDetails
  const orderDetailsCache = useRef({});

  const loadOrders = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const res = await fetchOrders({
        statusIds: [5, 6],
        startDate,
        endDate,
        page: 1,
        limit: 1000,
      });

      if (res.success && res.data) {
        setItems(res.data);
      } else {
        setItems([]);
      }
    } catch (error) {
      console.error("Error fetching orders:", error);
      setItems([]);
      setLoadError(true); // сүлжээ/серверийн алдаа — «захиалга байхгүй» биш
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, [startDate, endDate]);

  // ✅ Row дээр дарахад дэлгэрэнгүй татах
  const openOrderDetails = async (order) => {
    if (!order?.id) return;

    openModal("orderDetails");

    // ✅ Cache байвал backend дахиж дуудахгүй
    if (orderDetailsCache.current[order.id]) {
      setSelectedOrderDetails(orderDetailsCache.current[order.id]);
      return;
    }

    setDetailsLoading(true);
    setSelectedOrderDetails(null);

    try {
      const res = await fetchOrderDetails({ orderId: order.id });

      if (res.success && res.data) {
        orderDetailsCache.current[order.id] = res.data; // ✅ cache save
        setSelectedOrderDetails(res.data);
      } else {
        setSelectedOrderDetails(null);
      }
    } catch (err) {
      console.error("fetchOrderDetails error:", err);
      setSelectedOrderDetails(null);
    } finally {
      setDetailsLoading(false);
    }
  };

  const isEmpty = !loading && items.length === 0;

  return (
    <div className={`container-fluid ${isFullScreen && "fullScreenText"}`}>
      <CalendarModal
        show={currentModal === "calendar"}
        handleClose={closeModal}
        refetch={loadOrders}
        selectedModal={selectedModal}
        setStartDate={setStartDate}
        setEndDate={setEndDate}
      />

      <OrderDetailsModal
        show={currentModal === "orderDetails"}
        handleClose={closeModal}
        initialData={selectedOrderDetails}
        loading={detailsLoading}
      />

      <div className="row">
        <div className="col-12">
          {isFullScreen ? (
            <div className="fab">
              <div
                className="btn btn-primary btn-lg rounded-start-0"
                onClick={toggleFullScreen}
              >
                <i className="ri ri-fullscreen-exit-line" />
              </div>
            </div>
          ) : (
            <div className="page-title-box">
              <div className="page-title-right col-4">
                <form className="d-flex justify-content-end">
                  <a
                    className="btn btn-primary ms-1"
                    onClick={toggleFullScreen}
                  >
                    <i className="ri ri-fullscreen-line" />
                  </a>
                </form>
              </div>
              <h4 className="page-title">Хүргэсэн захиалгууд</h4>
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-8">
          <div className="card">
            <div className="card-body pt-1">
              {loadError && !loading && items.length === 0 ? (
                <div className="text-center py-5">
                  <i className="uil uil-exclamation-triangle text-danger" style={{ fontSize: 40 }} />
                  <p className="mt-2 mb-3">Захиалга татахад алдаа гарлаа.</p>
                  <button className="btn btn-outline-primary" onClick={loadOrders}>
                    Дахин оролдох
                  </button>
                </div>
              ) : isEmpty ? (
                <EmptyStateComponent title="Захиалга байхгүй байна" />
              ) : (
                <HistoryTable
                  items={items}
                  loading={loading}
                  onRowClick={openOrderDetails} // ✅ row click дамжуулна
                />
              )}
            </div>
          </div>
        </div>

        <div className="col-4 stick">
          <HistorySummary
            startDate={startDate}
            endDate={endDate}
            items={items}
            onCalendarOpen={() => openModal("calendar")}
            loading={loading}
            setSelectedModal={setSelectedModal}
          />
        </div>
      </div>
    </div>
  );
}
